import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  CustomFieldRenderProps,
  F0Dialog,
  F0Form,
  f0FormField,
  useF0Form,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import {
  AtsCreateJobPostingModalDocument,
  AtsNewJobPostingModalDocument,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'
import app from 'modules/core/store/models/app'

import { useSingleLegalEntitySelectorFormField } from 'modules/core/lib/formFields/legalEntitySelector'
import { useSingleLocationSelectorFormField } from 'modules/core/lib/formFields/locationSelector'
import { useSingleTeamSelectorFormField } from 'modules/core/lib/formFields/teamSelector'

import { JobCatalogRoleSelector } from 'modules/ats/components/job_catalog/JobCatalogRoleSelector'

import { NewJobPostingModalContentSkeleton } from './Skeleton'

const WORKPLACE_TYPES = ['onsite', 'hybrid', 'remote'] as const

interface FormValues {
  allowInternalApply: boolean
  allowReferral: boolean
  jobCatalog: {
    roleUuid: string | null
    levelUuid: string | null
  }
  legalEntityId: string
  locationId: string
  numberOfVacancies: number | undefined
  startDate: Date | undefined
  teamId: string | undefined
  title: string
  workplaceType: (typeof WORKPLACE_TYPES)[number] | undefined
}

const NewJobPostingModalPage = () => {
  const { company, access } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { data: jobData } = useQuery(AtsNewJobPostingModalDocument, {
    variables: {
      companyId: company?.id,
    },
  })

  const atsCompanyId = jobData?.ats.companiesConnection.nodes[0]?.id ?? 0 // How to do it correctly?

  const [createJobPosting] = useMutation(AtsCreateJobPostingModalDocument, {
    extractorFn: (data) => data.atsMutations.createAtsJobPosting,
  })

  const isJobPostingRevampFeatureEnabled =
    jobData?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_ats_job_posting_revamp'
    )?.enabled ?? false

  const isHiringPlanEnabled =
    jobData?.ats._policies.sidebarSeeRecruitmentHiringPlanSubLevelItem.can ?? false

  const jobCatalogRoles = useMemo(
    () => jobData?.jobCatalog.treeNodesConnection.nodes ?? [],
    [jobData?.jobCatalog.treeNodesConnection.nodes]
  )

  const locationField = useSingleLocationSelectorFormField({
    label: i18n.t('ats.jobs.new_job_modal.workplace_input_label'),
    placeholder: i18n.t('ats.jobs.new_job_modal.workplace_input_placeholder'),
    section: '',
    mandatory: true,
    row: 'location-team',
  })

  const teamField = useSingleTeamSelectorFormField({
    label: i18n.t('ats.jobs.new_job_modal.team_input_label'),
    placeholder: i18n.t('ats.jobs.new_job_modal.team_input_placeholder'),
    section: '',
    mandatory: false,
    row: 'location-team',
  })

  const legalEntityField = useSingleLegalEntitySelectorFormField({
    label: i18n.t('ats.jobs.new_job_modal.legal_entity_input_label'),
    placeholder: i18n.t('ats.jobs.new_job_modal.legal_entity__input_placeholder'),
    section: '',
    mandatory: true,
    row: 'legal-workplace',
  })

  const schema = useMemo(
    () =>
      z.object({
        title: f0FormField(z.string().min(1, i18n.t('common.validations.empty')), {
          label: i18n.t('ats.jobs.new_job_modal.title_input_label'),
          placeholder: i18n.t('ats.jobs.new_job_modal.title_input_placeholder'),
        }),
        jobCatalog: f0FormField(
          z.object({
            roleUuid: z.string().nullable(),
            levelUuid: z.string().nullable(),
          }),
          {
            fieldType: 'custom',
            label: '',
            renderIf: () => isJobPostingRevampFeatureEnabled && jobCatalogRoles.length > 0,
            render: (props: CustomFieldRenderProps<FormValues['jobCatalog']>) => (
              <JobCatalogRoleSelector
                onLevelSelect={(roleUuid, roleLevelUuid) => {
                  props.onChange({
                    roleUuid: roleUuid ?? null,
                    levelUuid: roleLevelUuid ?? null,
                  })
                }}
                jobCatalogRoles={jobCatalogRoles}
              />
            ),
          }
        ),
        locationId: locationField,
        teamId: teamField,
        numberOfVacancies: f0FormField(z.number().positive().optional(), {
          label: i18n.t('ats.jobs.new_job_modal.number_of_vacancies'),
          placeholder: '1',
          fieldType: 'number',
          row: 'vacancies-date',
          renderIf: () => !isHiringPlanEnabled,
        }),
        startDate: f0FormField(z.date().optional(), {
          label: i18n.t('ats.jobs.new_job_modal.start_date'),
          placeholder: i18n.t('ats.jobs.new_job_modal.start_date_placeholder'),
          row: 'vacancies-date',
          renderIf: () => !isHiringPlanEnabled,
        }),
        legalEntityId: legalEntityField,
        workplaceType: f0FormField(
          z.enum(WORKPLACE_TYPES, {
            required_error: i18n.t('common.validations.empty'),
          }),
          {
            label: i18n.t('ats.jobs.new_job_modal.workplace_type_input_label'),
            placeholder: i18n.t('ats.jobs.new_job_modal.workplace_type_input_placeholder'),
            fieldType: 'select',
            row: 'legal-workplace',
            options: [
              {
                label: i18n.t('ats.workplace_types.onsite'),
                value: 'onsite',
              },
              {
                label: i18n.t('ats.workplace_types.hybrid'),
                value: 'hybrid',
              },
              {
                label: i18n.t('ats.workplace_types.remote'),
                value: 'remote',
              },
            ],
          }
        ),
        allowReferral: f0FormField(z.boolean().default(false), {
          label: i18n.t('ats.job_details.promotion.open_positions.allow_referral_label'),
          fieldType: 'checkbox',
          row: 'checkboxes',
        }),
        allowInternalApply: f0FormField(z.boolean().default(false), {
          label: i18n.t('ats.job_details.promotion.open_positions.allow_internal_apply_label'),
          fieldType: 'checkbox',
          row: 'checkboxes',
        }),
      }),
    [
      isJobPostingRevampFeatureEnabled,
      isHiringPlanEnabled,
      jobCatalogRoles,
      locationField,
      teamField,
      legalEntityField,
    ]
  )

  const formDefinition = useF0FormDefinition({
    name: 'new-job-posting',
    schema,
    defaultValues: {
      allowInternalApply: false,
      allowReferral: false,
      jobCatalog: {
        roleUuid: null,
        levelUuid: null,
      },
      legalEntityId: '',
      locationId: '',
      numberOfVacancies: undefined,
      startDate: undefined,
      teamId: undefined,
      title: '',
      workplaceType: undefined,
    },
    submitConfig: { type: 'default', hideSubmitButton: true, hideActionBar: true },
    errorTriggerMode: 'on-submit',
    onSubmit: async ({ data: values }) => {
      let fieldErrors: Record<string, string> = {}

      const result = await createJobPosting({
        variables: {
          allowInternalApply: values.allowInternalApply,
          allowReferral: values.allowReferral,
          authorId: access?.id,
          companyId: company?.id,
          coverLetterRequirement: 'optional',
          cvRequirement: 'optional',
          jobCatalogNodeRolesUuid: values.jobCatalog.roleUuid,
          jobCatalogNodeLevelsUuid: values.jobCatalog.levelUuid,
          legalEntityId: values.legalEntityId,
          locationId: values.locationId,
          numberOfVacancies: values.numberOfVacancies ?? null,
          personalUrlRequirement: 'optional',
          phoneRequirement: 'optional',
          photoRequirement: 'optional',
          startDate: values.startDate ? i18nDateToString(values.startDate, 'backend') : null,
          status: 'draft',
          teamId: values.teamId,
          title: values.title,
          workplaceType: values.workplaceType,
        },
      })

      const handled = handleResult(result, (errors) => {
        fieldErrors = errors
      })

      track(trackingEvents.ats.createJobOpeningSubmit, {
        is_successful: handled.success,
        error_code: !handled.success && result.toString(),
        allowInternalApply: values.allowInternalApply,
        allowReferral: values.allowReferral,
        jobCatalogNodeRolesUuid: values.jobCatalog.roleUuid,
        jobCatalogNodeLevelsUuid: values.jobCatalog.levelUuid,
        legalEntityId: values.legalEntityId,
        locationId: values.locationId,
        numberOfVacancies: values.numberOfVacancies,
        startDate: values.startDate ? i18nDateToString(values.startDate, 'backend') : null,
        teamId: values.teamId,
        title: values.title,
        workplaceType: values.workplaceType,
        ats_company_id: atsCompanyId,
      })

      if (handled.success && handled.data?.jobPosting?.id) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        track(trackingEvents.ats.jobSettingsJobCreateModalNext)
        dispatch({ type: EventTypes.JOB_POSTING_CREATED })

        navigate(
          navigation.ats.jobs.jobsId.settings.fullPath({
            jobId: handled.data.jobPosting.id,
          })
        )

        return { success: true }
      }

      return {
        success: false,
        errors: fieldErrors,
      }
    },
  })

  const { formRef, submit, isSubmitting } = useF0Form()

  const onClose = () => {
    track(trackingEvents.ats.createJobOpeningCancel)
    navigate(navigation.ats.jobs.fullPath())
  }

  const loading = !jobData

  return (
    <F0Dialog
      onClose={onClose}
      isOpen
      title={i18n.t('ats.jobs.new_job_modal.title')}
      primaryAction={{
        label: i18n.t('ats.jobs.new_job_modal.create_button'),
        onClick: () => {
          submit().catch(() => {})
        },
        loading: isSubmitting,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      {loading ? (
        <NewJobPostingModalContentSkeleton />
      ) : (
        <F0Form formDefinition={formDefinition} formRef={formRef} />
      )}
    </F0Dialog>
  )
}

export default NewJobPostingModalPage
